import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  CheckCircle2, Building2, User, FileText, Send, AlertCircle, 
  ClipboardCheck, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast, Toaster } from "sonner";
import axios from "axios";
import PetrocontrolLogo from "@/components/PetrocontrolLogo";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function DiagnosticoClientePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState(null);
  
  const [diagnosticoData, setDiagnosticoData] = useState(null);
  const [respuestas, setRespuestas] = useState({});
  const [observaciones, setObservaciones] = useState("");

  useEffect(() => {
    fetchDiagnostico();
  }, [token]);

  const fetchDiagnostico = async () => {
    try {
      const res = await axios.get(`${API}/diagnostico/${token}`);
      setDiagnosticoData(res.data);
      // Inicializar respuestas vacías
      const initialRespuestas = {};
      res.data.diagnostico_preguntas?.forEach(p => {
        initialRespuestas[p.id] = "";
      });
      setRespuestas(initialRespuestas);
    } catch (e) {
      setError(e.response?.data?.detail || "Error al cargar el diagnóstico");
    } finally {
      setLoading(false);
    }
  };

  const handleRespuestaChange = (preguntaId, valor) => {
    setRespuestas(prev => ({
      ...prev,
      [preguntaId]: valor
    }));
  };

  const handleSubmit = async () => {
    // Validar que todas las preguntas estén respondidas
    const preguntasSinResponder = diagnosticoData.diagnostico_preguntas?.filter(
      p => !respuestas[p.id] || respuestas[p.id].trim() === ""
    );
    
    if (preguntasSinResponder?.length > 0) {
      toast.error("Por favor responda todas las preguntas");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/diagnostico/${token}/submit`, {
        respuestas,
        observaciones
      });
      setCompleted(true);
      toast.success("Diagnóstico enviado correctamente");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar el diagnóstico");
    } finally {
      setSubmitting(false);
    }
  };

  const renderPregunta = (pregunta) => {
    const { id, pregunta: texto, tipo, opciones } = pregunta;
    
    switch (tipo) {
      case "texto":
        return (
          <div key={id} className="space-y-2">
            <Label className="text-gray-700 font-medium">{texto}</Label>
            <Input
              value={respuestas[id] || ""}
              onChange={(e) => handleRespuestaChange(id, e.target.value)}
              placeholder="Escriba su respuesta..."
              className="border-gray-300"
            />
          </div>
        );
      
      case "numero":
        return (
          <div key={id} className="space-y-2">
            <Label className="text-gray-700 font-medium">{texto}</Label>
            <Input
              type="number"
              value={respuestas[id] || ""}
              onChange={(e) => handleRespuestaChange(id, e.target.value)}
              placeholder="Ingrese un número..."
              className="border-gray-300"
            />
          </div>
        );
      
      case "select":
        return (
          <div key={id} className="space-y-2">
            <Label className="text-gray-700 font-medium">{texto}</Label>
            <Select value={respuestas[id] || ""} onValueChange={(v) => handleRespuestaChange(id, v)}>
              <SelectTrigger className="border-gray-300">
                <SelectValue placeholder="Seleccione una opción..." />
              </SelectTrigger>
              <SelectContent>
                {opciones?.map(op => (
                  <SelectItem key={op} value={op}>{op}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      
      case "si_no":
        return (
          <div key={id} className="space-y-2">
            <Label className="text-gray-700 font-medium">{texto}</Label>
            <RadioGroup
              value={respuestas[id] || ""}
              onValueChange={(v) => handleRespuestaChange(id, v)}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="si" id={`${id}-si`} />
                <Label htmlFor={`${id}-si`} className="cursor-pointer">Sí</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id={`${id}-no`} />
                <Label htmlFor={`${id}-no`} className="cursor-pointer">No</Label>
              </div>
            </RadioGroup>
          </div>
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f7f3] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#1a5c4a] mx-auto mb-4" />
          <p className="text-gray-600">Cargando diagnóstico...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#f8f7f3] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => navigate("/")} variant="outline">
              Ir al inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-[#f8f7f3] flex items-center justify-center p-4">
        <Toaster position="top-right" richColors />
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">¡Diagnóstico Enviado!</h2>
            <p className="text-gray-600 mb-6">
              Gracias por completar el diagnóstico. Nuestro equipo revisará sus respuestas 
              y se pondrá en contacto con usted a la brevedad.
            </p>
            <div className="bg-[#f8f7f3] rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600">
                <strong>Empresa:</strong> {diagnosticoData?.company_name}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Servicio:</strong> {diagnosticoData?.service_type}
              </p>
            </div>
            <Button onClick={() => navigate("/")} className="bg-[#1a5c4a] hover:bg-[#164a3d]">
              Ir al inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f7f3]">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <PetrocontrolLogo className="h-10" />
          <span className="text-sm text-gray-500">Diagnóstico Regulatorio</span>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        <Card className="border-none shadow-lg">
          <CardHeader className="bg-[#1a5c4a] text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-3">
              <ClipboardCheck className="h-6 w-6" />
              Formulario de Diagnóstico
            </CardTitle>
          </CardHeader>
          
          <CardContent className="p-6">
            {/* Info del cliente */}
            <div className="bg-[#f8f7f3] rounded-lg p-4 mb-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-[#1a5c4a]" />
                  <span className="text-sm"><strong>Nombre:</strong> {diagnosticoData?.full_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-[#1a5c4a]" />
                  <span className="text-sm"><strong>Empresa:</strong> {diagnosticoData?.company_name || "N/A"}</span>
                </div>
                <div className="flex items-center gap-2 md:col-span-2">
                  <FileText className="h-4 w-4 text-[#1a5c4a]" />
                  <span className="text-sm"><strong>Servicio de interés:</strong> {diagnosticoData?.service_type}</span>
                </div>
              </div>
            </div>

            {/* Instrucciones */}
            <div className="mb-6">
              <p className="text-gray-600 text-sm">
                Por favor complete las siguientes preguntas para que podamos realizar un diagnóstico 
                adecuado de sus necesidades regulatorias. Toda la información proporcionada será 
                tratada de manera confidencial.
              </p>
            </div>

            {/* Preguntas */}
            <div className="space-y-6">
              {diagnosticoData?.diagnostico_preguntas?.map(renderPregunta)}
              
              {/* Observaciones adicionales */}
              <div className="space-y-2 pt-4 border-t">
                <Label className="text-gray-700 font-medium">
                  Observaciones o comentarios adicionales (opcional)
                </Label>
                <Textarea
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  placeholder="Si tiene alguna pregunta o información adicional que desee compartir..."
                  rows={4}
                  className="border-gray-300"
                />
              </div>
            </div>

            {/* Botón enviar */}
            <div className="mt-8 flex justify-end">
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-[#1a5c4a] hover:bg-[#164a3d] px-8"
                data-testid="submit-diagnostico-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Finalizar y Enviar
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer info */}
        <p className="text-center text-xs text-gray-500 mt-6">
          Al enviar este formulario, acepta que PETROCONTROL procese su información 
          para fines de diagnóstico y asesoría regulatoria.
        </p>
      </main>
    </div>
  );
}
